import React from 'react';
import { useAuth } from '../../auth';
import { MANUAL_LOGOUT_EVENT } from '../../auth/AuthContext';
import { setGlobalFetchErrorListener, installFetchInterceptor } from './globalFetchInterceptor';

export interface GlobalError {
  title: string;
  message: string;
  isAuthError?: boolean;
  reason?: 'hard-timeout' | 'exchange-failure';
  authType?: 'INTERNAL' | 'EXTERNAL';
}

interface ErrorContextValue {
  error: GlobalError | null;
  setError: (error: GlobalError | null) => void;
  clearError: () => void;
  handleReauth: () => void;
}

const ErrorContext = React.createContext<ErrorContextValue | undefined>(undefined);

export const ErrorProvider = ({ children }: { children: React.ReactNode }) => {
  const [error, setErrorState] = React.useState<GlobalError | null>(null);
  const { reauthenticate, session } = useAuth();
  const suppressSessionUiRef = React.useRef(false);

  React.useEffect(() => {
    if (session) {
      suppressSessionUiRef.current = false;
    }
  }, [session]);

  // Auth errors must never be overwritten by non-auth errors.
  // Use the functional updater so the guard runs against the latest queued state,
  // even when multiple setError calls are batched in the same React render cycle.
  const setError = React.useCallback((nextError: GlobalError | null) => {
    setErrorState((prev) => {
      if (prev?.isAuthError && !nextError?.isAuthError) {
        return prev;
      }
      return nextError;
    });
  }, []);

  // Register listener before interceptor install so early responses are not missed.
  React.useEffect(() => {
    const showSessionError = (nextError: GlobalError) => {
      if (!suppressSessionUiRef.current) {
        setError(nextError);
      }
    };

    setGlobalFetchErrorListener((err) => {
      if (!suppressSessionUiRef.current || !err.isAuthError) {
        setError({
          title: err.title,
          message: err.message,
          isAuthError: err.isAuthError,
        });
      }
    });

    installFetchInterceptor();

    // Listen for mandatory session expiry dispatched by AuthContext (60-minute hard timeout).
    const handleForceLogout = (event: Event) => {
      const detail = (event as CustomEvent<{ reason?: string; authType?: string }>).detail;
      const reason = detail?.reason;
      // EXTERNAL users have no Azure Entra ID account to re-authenticate with —
      // they can only be told to close this tab, not to click Re-authenticate.
      const isExternal = detail?.authType === 'EXTERNAL';
      showSessionError({
        title: 'Session Limit Reached',
        message: isExternal
          ? 'Your session has reached the 60-minute limit and you have been signed out. Close this tab to continue.'
          : 'Your session has reached the 60-minute limit and you have been signed out. Click Re-authenticate to continue.',
        isAuthError: true,
        reason: reason === 'hard-timeout' ? 'hard-timeout' : undefined,
        authType: isExternal ? 'EXTERNAL' : undefined,
      });
    };

    // Listen for soft refresh-failure warnings dispatched by AuthContext.
    // No auto-logout is scheduled here — this is informational only.
    // The user can continue working; the hard-timeout path handles forced logout.
    const handleRefreshWarning = () => {
      showSessionError({
        title: 'Session Renewal Issue',
        message: 'We could not silently renew your session. You can continue working, but you may be asked to sign in again if your session expires. Your session will automatically end at the 60-minute mark.',
        isAuthError: false,
      });
    };

    // Listen for the 3-minute advance warning before hard logout (fires at 57 minutes).
    const handleSessionWarning = () => {
      showSessionError({
        title: 'Session Expiring Soon',
        message: 'Your session will end in approximately 3 minutes. Please save your work now.',
        isAuthError: false,
      });
    };

    const handleManualLogout = () => {
      suppressSessionUiRef.current = true;
      setErrorState(null);
    };

    window.addEventListener('rfi:session:force-logout', handleForceLogout);
    window.addEventListener('rfi:session:refresh-warning', handleRefreshWarning);
    window.addEventListener('rfi:session:limit-warning', handleSessionWarning);
    window.addEventListener(MANUAL_LOGOUT_EVENT, handleManualLogout);

    return () => {
      setGlobalFetchErrorListener(null);
      window.removeEventListener('rfi:session:force-logout', handleForceLogout);
      window.removeEventListener('rfi:session:refresh-warning', handleRefreshWarning);
      window.removeEventListener('rfi:session:limit-warning', handleSessionWarning);
      window.removeEventListener(MANUAL_LOGOUT_EVENT, handleManualLogout);
    };
  }, [setError]);

  const clearError = React.useCallback(() => {
    setErrorState(null);
  }, []);

  const handleReauth = React.useCallback(() => {
    clearError();
    reauthenticate();
  }, [clearError, reauthenticate]);

  const value: ErrorContextValue = {
    error,
    setError,
    clearError,
    handleReauth,
  };

  return <ErrorContext.Provider value={value}>{children}</ErrorContext.Provider>;
};

export const useGlobalError = () => {
  const context = React.useContext(ErrorContext);
  if (!context) {
    throw new Error('useGlobalError must be used inside ErrorProvider');
  }
  return context;
};
